import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Tabs,
  Row,
  Col,
  Badge,
  Tooltip,
  Avatar,
  Spin,
  DatePicker,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  MessageOutlined,
  BellOutlined,
  SendOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  NotificationOutlined,
  GlobalOutlined,
  MobileOutlined,
  ShopOutlined,
  TruckOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Option } = Select;

const fmtInt = (v) => Number(v || 0).toLocaleString('en-IN');

const StatTile = ({ icon, label, value }) => (
  <div className="kpi">
    <div className="kpi-icon">{icon}</div>
    <div className="kpi-body">
      <span className="kpi-value">{value}</span>
      <span className="kpi-label">{label}</span>
    </div>
  </div>
);

const Chat = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isMessageModalVisible, setIsMessageModalVisible] = useState(false);
  const [isNotificationModalVisible, setIsNotificationModalVisible] = useState(false);
  const [viewingConversation, setViewingConversation] = useState(null);
  const [editingNotification, setEditingNotification] = useState(null);
  const [messageForm] = Form.useForm();
  const [notificationForm] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [activeTab, setActiveTab] = useState('conversations');
  const [realtimeMessages] = useState([]);

  const getMediaUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `/storage/${url.replace(/^\/?(storage\/)?/, '')}`;
  };

  const renderMessageContent = (msg) => {
    const isImage = msg.message_type === 'image';
    const isVideo = msg.message_type === 'video';
    const isVoice = msg.message_type === 'voice';
    const isLocation = msg.message_type === 'location';

    if (isImage) {
      const imgUrl = msg.image_url || msg.message;
      return (
        <div>
          <img
            src={getMediaUrl(imgUrl)}
            alt="Sent"
            style={{ maxWidth: 260, borderRadius: 10, display: 'block' }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          {msg.message && msg.message !== 'Image' && (
            <div style={{ fontSize: 13, marginTop: 4 }}>{msg.message}</div>
          )}
        </div>
      );
    }

    if (isVideo) {
      const vidUrl = msg.video_url || msg.message;
      return (
        <div>
          <video
            controls
            style={{ maxWidth: 260, maxHeight: 200, borderRadius: 10, display: 'block' }}
          >
            <source src={getMediaUrl(vidUrl)} />
          </video>
          {msg.message && msg.message !== 'Video' && (
            <div style={{ fontSize: 13, marginTop: 4 }}>{msg.message}</div>
          )}
        </div>
      );
    }

    if (isVoice) {
      const audioUrl = msg.voice_url || msg.message;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <audio controls style={{ maxWidth: 200, height: 36 }}>
            <source src={getMediaUrl(audioUrl)} />
          </audio>
        </div>
      );
    }

    if (isLocation) {
      let locationData = msg.location_data;
      if (typeof locationData === 'string') {
        try {
          locationData = JSON.parse(locationData);
        } catch {
          locationData = null;
        }
      }
      const lat = locationData?.latitude;
      const lng = locationData?.longitude;
      const address = locationData?.address || locationData?.name || 'Shared Location';
      const mapUrl = lat && lng ? `https://maps.google.com/?q=${lat},${lng}` : null;

      return (
        <div style={{ padding: 8, background: '#e6f7ff', borderRadius: 8, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <EnvironmentOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 500 }}>Location</div>
              <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{address}</div>
              {mapUrl && (
                <a
                  href={mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 11, color: '#1890ff', marginTop: 4, display: 'inline-block' }}
                >
                  View on map
                </a>
              )}
            </div>
          </div>
        </div>
      );
    }

    return <div style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</div>;
  };
  const queryClient = useQueryClient();

  // Pusher disabled for now - causing infinite reload
  // useEffect(() => {
  //   initPusher();
  //
  //   subscribeToChannel('admin-chat', (data) => {
  //     if (data.message) {
  //       setRealtimeMessages(prev => [...prev, data.message]);
  //       message.info(`New message from ${data.message.sender?.full_name || 'Unknown'}`);
  //       queryClient.invalidateQueries('conversations');
  //       queryClient.invalidateQueries('chat-stats');
  //     }
  //   });

  //   return () => {
  //   };
  // }, [queryClient]);

  // Fetch conversation messages when viewing a conversation
  const { data: conversationMessages = [], isLoading: messagesLoading } = useQuery(
    ['conversation-messages', viewingConversation?.id],
    async () => {
      if (!viewingConversation?.id) return [];
      const response = await api.get(
        `/admin/chat/conversations/${viewingConversation.id}/messages`,
      );
      const data = response.data?.data || response.data || [];
      return Array.isArray(data) ? data : [];
    },
    {
      enabled: !!viewingConversation?.id,
    },
  );

  // Combine stored messages with real-time messages
  const allMessages = [
    ...conversationMessages,
    ...realtimeMessages.filter(
      (msg) =>
        msg.conversation_id === viewingConversation?.id ||
        msg.sender_id === viewingConversation?.customer?.id ||
        msg.receiver_id === viewingConversation?.customer?.id,
    ),
  ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  // Fetch conversations with filters
  const { data: conversations = [], isLoading } = useQuery(
    ['conversations', searchTerm, statusFilter],
    async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);

      const response = await api.get(`/admin/chat/conversations?${params.toString()}`);
      const data = response.data?.data || response.data || [];
      return Array.isArray(data) ? data : [];
    },
    {
      onError: (_error) => {
        console.error('Failed to load conversations:', _error);
        message.error('Failed to load conversations');
      },
    },
  );

  // Debug: Log conversations data
  useEffect(() => {}, [conversations]);

  // Fetch notifications
  const { data: notifications = [], isLoading: notificationsLoading } = useQuery(
    'notifications',
    async () => {
      const response = await api.get('/admin/admin-notifications');
      const data = response.data?.data || response.data || [];
      return Array.isArray(data) ? data : [];
    },
    {
      onError: (_error) => {
        message.error('Failed to load notifications');
      },
    },
  );

  // Fetch chat statistics
  const { data: chatStats } = useQuery(
    'chat-stats',
    async () => {
      const response = await api.get('/admin/chat/stats');
      const data = response.data?.data || response.data || {};
      return data;
    },
    {
      staleTime: 0,
      refetchOnMount: true,
    },
  );

  // Send message mutation
  const sendMessageMutation = useMutation(
    async ({ conversationId, message }) => {
      const response = await api.post(`/admin/chat/conversations/${conversationId}/messages`, {
        message: message,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Message sent successfully');
        queryClient.invalidateQueries('conversations');
        setIsMessageModalVisible(false);
        messageForm.resetFields();
      },
      onError: (_error) => {
        message.error('Failed to send message');
      },
    },
  );

  // Create notification mutation
  const createNotificationMutation = useMutation(
    async (values) => {
      const response = await api.post('/admin/admin-notifications/send', values);
      return response.data;
    },
    {
      onSuccess: (data) => {
        message.success(data.message || 'Notification created successfully');
        queryClient.invalidateQueries('notifications');
        queryClient.invalidateQueries('chat-stats');
        setIsNotificationModalVisible(false);
        notificationForm.resetFields();
      },
      onError: (error) => {
        message.error(
          'Failed to send notification: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  // Delete notification mutation
  const deleteNotificationMutation = useMutation(
    async (id) => {
      await api.delete(`/admin/admin-notifications/${id}`);
    },
    {
      onSuccess: () => {
        message.success('Notification deleted successfully');
        queryClient.invalidateQueries('notifications');
      },
      onError: (_error) => {
        message.error('Failed to delete notification');
      },
    },
  );

  // Handle functions
  const handleViewConversation = (record) => {
    setViewingConversation(record);
    setIsModalVisible(true);
  };

  const handleSendMessage = (record) => {
    setViewingConversation(record);
    setIsMessageModalVisible(true);
  };

  const handleCreateNotification = () => {
    setEditingNotification(null);
    notificationForm.resetFields();
    setIsNotificationModalVisible(true);
  };

  const handleEditNotification = (record) => {
    setEditingNotification(record);
    notificationForm.setFieldsValue(record);
    setIsNotificationModalVisible(true);
  };

  const handleDeleteNotification = (record) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this notification?',
      content: `Notification: ${record.title}`,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => {
        deleteNotificationMutation.mutate(record.id);
      },
    });
  };

  const handleSendMessageSubmit = (values) => {
    sendMessageMutation.mutate({
      conversationId: viewingConversation.id,
      message: values.message,
    });
  };

  const handleNotificationSubmit = (values) => {
    if (editingNotification) {
      message.info('Update notification functionality coming soon');
    } else {
      // Transform channels array to send_push, send_email, send_sms
      const channels = values.channels || [];
      const payload = {
        title: values.title,
        message: values.message,
        type: values.type || 'system',
        target_type: values.target_type || 'all',
        send_push: channels.includes('push'),
        send_email: channels.includes('email'),
        send_sms: channels.includes('sms'),
        priority: values.priority || 'normal',
      };
      createNotificationMutation.mutate(payload);
    }
  };

  const conversationColumns = [
    {
      title: 'Users',
      key: 'users',
      render: (_, record) => {
        const customer = record.customer;
        const otherUser = record.other_user || record.admin;
        const participants = record.participants || [];

        const typeColors = {
          customer: 'blue',
          provider: 'purple',
          delivery: 'cyan',
          admin: 'green',
        };

        return (
          <Space direction="vertical" size={0}>
            {customer && (
              <Space>
                <Avatar size="small" icon={<UserOutlined />} />
                <span>{customer.name || customer.full_name || 'N/A'}</span>
                <Tag color={typeColors[customer.user_type]}>
                  {customer.user_type?.toUpperCase()}
                </Tag>
              </Space>
            )}
            {otherUser && (
              <Space>
                <span style={{ color: '#999' }}>↳ Chatting with: </span>
                <Avatar size="small" icon={<UserOutlined />} />
                <span>{otherUser.name || otherUser.full_name || 'N/A'}</span>
                <Tag color={typeColors[otherUser.user_type]}>
                  {otherUser.user_type?.toUpperCase()}
                </Tag>
              </Space>
            )}
            {!customer && !otherUser && participants.length > 0 && (
              <Space>
                <Avatar size="small" icon={<UserOutlined />} />
                <span>{participants[0]?.name || 'N/A'}</span>
                <Tag color={typeColors[participants[0]?.type]}>
                  {participants[0]?.type?.toUpperCase()}
                </Tag>
              </Space>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Last Message',
      dataIndex: 'last_message',
      key: 'last_message',
      render: (message) => (
        <Tooltip title={message}>
          <span
            style={{
              maxWidth: 200,
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {message || 'No messages'}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colorMap = {
          active: 'green',
          closed: 'default',
          pending: 'orange',
        };
        return <Tag color={colorMap[status] || 'default'}>{status?.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Unread',
      dataIndex: 'unread_count',
      key: 'unread_count',
      render: (count) => (
        <Badge count={count} showZero color="red">
          <MessageOutlined />
        </Badge>
      ),
    },
    {
      title: 'Last Activity',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date) => (date ? dayjs(date).format('MMM DD, YYYY HH:mm') : 'Never'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Conversation">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewConversation(record)}
            />
          </Tooltip>
          <Tooltip title="Send Message">
            <Button type="text" icon={<SendOutlined />} onClick={() => handleSendMessage(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const notificationColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title) => <strong>{title}</strong>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const iconMap = {
          info: <NotificationOutlined />,
          warning: <ExclamationCircleOutlined />,
          error: <CloseCircleOutlined />,
          success: <CheckCircleOutlined />,
        };
        const colorMap = {
          info: 'blue',
          warning: 'orange',
          error: 'red',
          success: 'green',
        };
        return (
          <Tag color={colorMap[type]} icon={iconMap[type]}>
            {type?.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Target',
      dataIndex: 'target',
      key: 'target',
      render: (target, record) => {
        if (record.recipient_name) {
          return (
            <Space>
              <UserOutlined />
              <span>{record.recipient_name}</span>
            </Space>
          );
        }
        const iconMap = {
          all: <GlobalOutlined />,
          users: <UserOutlined />,
          providers: <ShopOutlined />,
          delivery: <TruckOutlined />,
        };
        return (
          <Space>
            {iconMap[target]}
            <span>{target?.toUpperCase() || 'ALL'}</span>
          </Space>
        );
      },
    },
    {
      title: 'Channels',
      dataIndex: 'channels',
      key: 'channels',
      render: (channels) => {
        if (!channels) return '-';
        return (
          <Space>
            {channels.push && (
              <Tooltip title="Push">
                <MobileOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            )}
            {channels.email && (
              <Tooltip title="Email">
                <MailOutlined style={{ color: '#52c41a' }} />
              </Tooltip>
            )}
            {channels.sms && (
              <Tooltip title="SMS">
                <PhoneOutlined style={{ color: '#fa8c16' }} />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colorMap = {
          draft: 'default',
          scheduled: 'blue',
          sent: 'green',
          failed: 'red',
        };
        return <Tag color={colorMap[status] || 'default'}>{status?.toUpperCase() || 'SENT'}</Tag>;
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).fromNow(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditNotification(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteNotification(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Chat Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={12} md={6}>
          <StatTile
            icon={<MessageOutlined />}
            label="Total Conversations"
            value={fmtInt(chatStats?.total_conversations)}
          />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <StatTile
            icon={<CheckCircleOutlined />}
            label="Active Conversations"
            value={fmtInt(chatStats?.active_conversations)}
          />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <StatTile
            icon={<BellOutlined />}
            label="Unread Messages"
            value={fmtInt(chatStats?.unread_messages)}
          />
        </Col>
        <Col xs={12} sm={12} md={6}>
          <StatTile
            icon={<NotificationOutlined />}
            label="Total Notifications"
            value={fmtInt(chatStats?.total_notifications)}
          />
        </Col>
      </Row>

      {/* Main Content */}
      <Card
        title="Conversations & Notifications"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateNotification}>
            Create Notification
          </Button>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'conversations',
              label: 'Conversations',
              children: (
                <>
                  <div
                    style={{
                      display: 'flex',
                      gap: 12,
                      flexWrap: 'wrap',
                      marginBottom: 16,
                    }}
                  >
                    <Input.Search
                      placeholder="Search conversations..."
                      allowClear
                      style={{ maxWidth: 280 }}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <Select
                      placeholder="Filter by status"
                      allowClear
                      style={{ width: 170 }}
                      onChange={setStatusFilter}
                      options={[
                        { label: 'Active', value: 'active' },
                        { label: 'Closed', value: 'closed' },
                        { label: 'Pending', value: 'pending' },
                      ]}
                    />
                  </div>
                  <Table
                    columns={conversationColumns}
                    dataSource={conversations}
                    rowKey="id"
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} conversations`,
                    }}
                    scroll={{ x: 800 }}
                  />
                </>
              ),
            },
            {
              key: 'notifications',
              label: 'Notifications',
              children: (
                <Table
                  columns={notificationColumns}
                  dataSource={notifications}
                  loading={notificationsLoading}
                  rowKey="id"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} notifications`,
                  }}
                />
              ),
            },
          ]}
        />
      </Card>

      {/* Conversation Details Modal */}
      <Modal
        title="Conversation Details"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setViewingConversation(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsModalVisible(false);
              setViewingConversation(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={800}
      >
        {viewingConversation && (
          <div style={{ padding: '20px 0' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small">
                  <h4>Conversation Participants</h4>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {(viewingConversation.participants || []).map((p, i) => (
                      <div key={i}>
                        <Space>
                          <Avatar size="small" icon={<UserOutlined />} />
                          <strong>{p.name || p.full_name || 'N/A'}</strong>
                          <Tag
                            color={
                              {
                                customer: 'blue',
                                provider: 'purple',
                                delivery: 'cyan',
                                admin: 'green',
                              }[p.user_type]
                            }
                          >
                            {p.user_type?.toUpperCase()}
                          </Tag>
                        </Space>
                        <div style={{ marginLeft: 28, color: '#666', fontSize: 12 }}>
                          {p.email} {p.phone ? `| ${p.phone}` : ''}
                        </div>
                      </div>
                    ))}
                    <div>
                      <strong>Conversation ID:</strong> #{viewingConversation.id}
                    </div>
                    <div>
                      <strong>Status:</strong>
                      <Tag color={viewingConversation.status === 'active' ? 'green' : 'default'}>
                        {viewingConversation.status?.toUpperCase()}
                      </Tag>
                    </div>
                    <div>
                      <strong>Started:</strong>{' '}
                      {viewingConversation.created_at
                        ? new Date(viewingConversation.created_at).toLocaleString()
                        : 'N/A'}
                    </div>
                    <div>
                      <strong>Last Activity:</strong>{' '}
                      {viewingConversation.updated_at
                        ? new Date(viewingConversation.updated_at).toLocaleString()
                        : 'Never'}
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <h4>Conversation Stats</h4>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <strong>Total Messages:</strong> {viewingConversation.messages_count || 0}
                    </div>
                    <div>
                      <strong>Unread Messages:</strong> {viewingConversation.unread_count || 0}
                    </div>
                    <div>
                      <strong>Response Time:</strong>{' '}
                      {viewingConversation.average_response_time || 'N/A'}
                    </div>
                    <div>
                      <strong>Satisfaction:</strong>{' '}
                      {viewingConversation.satisfaction_score
                        ? `${viewingConversation.satisfaction_score}/5`
                        : 'Not rated'}
                    </div>
                  </Space>
                </Card>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Card size="small">
                  <h4>Recent Messages</h4>
                  <div
                    style={{
                      height: 300,
                      overflow: 'auto',
                      background: '#f5f5f5',
                      borderRadius: 8,
                      padding: 16,
                    }}
                  >
                    {messagesLoading ? (
                      <div style={{ textAlign: 'center', color: '#999', marginTop: 100 }}>
                        <MessageOutlined style={{ fontSize: 48 }} />
                        <div style={{ marginTop: 16 }}>Loading conversation messages...</div>
                      </div>
                    ) : allMessages.length > 0 ? (
                      <div>
                        {allMessages.map((msg, index) => (
                          <div
                            key={msg.id || index}
                            style={{
                              textAlign:
                                msg.sender_id === viewingConversation.customer?.id
                                  ? 'left'
                                  : 'right',
                              marginBottom: 12,
                            }}
                          >
                            <div
                              style={{
                                display: 'inline-block',
                                maxWidth: '70%',
                                padding: '8px 12px',
                                borderRadius: 12,
                                background:
                                  msg.sender_id === viewingConversation.customer?.id
                                    ? '#fff'
                                    : '#6d28d9',
                                color:
                                  msg.sender_id === viewingConversation.customer?.id
                                    ? '#000'
                                    : '#fff',
                              }}
                            >
                              <div style={{ fontWeight: 'bold', fontSize: 11, marginBottom: 2 }}>
                                {msg.sender?.name || msg.sender?.full_name || 'Unknown'}
                              </div>
                              {renderMessageContent(msg)}
                              <div style={{ fontSize: 10, opacity: 0.7, marginTop: 4 }}>
                                {msg.created_at ? new Date(msg.created_at).toLocaleString() : ''}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', color: '#999', marginTop: 100 }}>
                        <MessageOutlined style={{ fontSize: 48 }} />
                        <div style={{ marginTop: 16 }}>No messages in this conversation</div>
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Send Message Modal */}
      <Modal
        title="Send Message"
        open={isMessageModalVisible}
        onCancel={() => {
          setIsMessageModalVisible(false);
          setViewingConversation(null);
          messageForm.resetFields();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsMessageModalVisible(false);
              setViewingConversation(null);
              messageForm.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button key="send" type="primary" onClick={() => messageForm.submit()}>
            Send Message
          </Button>,
        ]}
      >
        {viewingConversation && (
          <div style={{ padding: '20px 0' }}>
            <Form form={messageForm} layout="vertical" onFinish={handleSendMessageSubmit}>
              <Form.Item label="To">
                <Input
                  disabled
                  value={
                    (viewingConversation.other_user || viewingConversation.admin)?.name ||
                    (viewingConversation.other_user || viewingConversation.admin)?.full_name ||
                    viewingConversation.customer?.name ||
                    viewingConversation.customer?.full_name ||
                    'N/A'
                  }
                />
              </Form.Item>
              <Form.Item
                label="Message"
                name="message"
                rules={[{ required: true, message: 'Please enter your message' }]}
              >
                <Input.TextArea rows={4} placeholder="Type your message here..." />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* Notification Modal */}
      <Modal
        title={editingNotification ? 'Edit Notification' : 'Create Notification'}
        open={isNotificationModalVisible}
        onCancel={() => {
          setIsNotificationModalVisible(false);
          setEditingNotification(null);
          notificationForm.resetFields();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsNotificationModalVisible(false);
              setEditingNotification(null);
              notificationForm.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={() => notificationForm.submit()}>
            {editingNotification ? 'Update' : 'Create'}
          </Button>,
        ]}
        width={600}
      >
        <Form form={notificationForm} layout="vertical" onFinish={handleNotificationSubmit}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Title"
                name="title"
                rules={[{ required: true, message: 'Please enter notification title' }]}
              >
                <Input placeholder="Enter notification title" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Type"
                name="type"
                rules={[{ required: true, message: 'Please select notification type' }]}
              >
                <Select placeholder="Select notification type">
                  <Option value="info">Info</Option>
                  <Option value="warning">Warning</Option>
                  <Option value="error">Error</Option>
                  <Option value="success">Success</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Target Type"
                name="target_type"
                rules={[{ required: true, message: 'Please select target type' }]}
              >
                <Select placeholder="Select target type">
                  <Option value="all">All Users</Option>
                  <Option value="users">Customers</Option>
                  <Option value="providers">Providers</Option>
                  <Option value="delivery">Delivery Personnel</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Message"
                name="message"
                rules={[{ required: true, message: 'Please enter notification message' }]}
              >
                <Input.TextArea rows={4} placeholder="Enter notification message..." />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Channels"
                name="channels"
                rules={[{ required: true, message: 'Please select at least one channel' }]}
              >
                <Select mode="multiple" placeholder="Select notification channels">
                  <Option value="push">Push Notification</Option>
                  <Option value="email">Email</Option>
                  <Option value="sms">SMS</Option>
                  <Option value="in_app">In-App</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: 'Please select status' }]}
              >
                <Select placeholder="Select status">
                  <Option value="draft">Draft</Option>
                  <Option value="scheduled">Scheduled</Option>
                  <Option value="sent">Send Immediately</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Schedule Time" name="scheduled_at">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default Chat;
